import { classNames, Component, h, tag } from "omi";

import { NWIcon } from "@/components/atoms/nw-icon";
import { openLoginModal, openModal, openProModal } from "@/components/modals";
import { tailwind } from "@/tailwind";

import { PortfolioDocument } from "../../store/models";
import { PortfolioStore } from "../../store";
import { GridTile } from "./components/tile";
import { PortfolioGridSkeleton } from "./skeleton";

type Props = {
  documents: PortfolioDocument[];
  isLoading: boolean;
  showAnonState?: boolean;
};

@tag("portfolio-grid")
export class ProjectGrid extends Component<Props> {
  static css = [tailwind, `:host { width: 100%; }`];

  private openUploadModal = () => {
    openModal("portfolioUpload", { type: "upload" });
  };

  private renderEditableGrid({ documents }: Props) {
    const isEditable = PortfolioStore.isEditable();
    const hasReachedProLimit = PortfolioStore.hasReachedProLimit();
    const hasDocuments = documents.length > 0;

    if (!isEditable) return null;

    return (
      <>
        {!hasReachedProLimit && (
          <AddItemTile
            hasDocuments={hasDocuments}
            onClickHandler={this.openUploadModal}
            documentsLength={documents.length}
          />
        )}
        {hasReachedProLimit && <LimitReachedTile documentsLength={documents.length} />}
      </>
    );
  }

  render({ showAnonState = false, documents, isLoading }: Props) {
    if (isLoading) {
      return <PortfolioGridSkeleton />;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-6 lg:gap-16 mx-auto animate-in slide fade-in slide-in-from-bottom-5 ease-out duration-500 px-4">
        {showAnonState && <AddItemTile hasDocuments={false} documentsLength={0} onClickHandler={openLoginModal} />}
        {documents.map(document => (
          <GridTile key={document.id} name={document.title} data={document} />
        ))}
        {this.renderEditableGrid({ documents, isLoading })}
      </div>
    );
  }
}

function AddItemTile({
  hasDocuments,
  onClickHandler,
  documentsLength = 0,
}: {
  hasDocuments: boolean;
  onClickHandler: () => void;
  documentsLength?: number;
}) {
  return (
    <button
      className={classNames(
        "border-2 border-dashed border-gray-200 rounded-[48px] flex p-4 items-center justify-center w-full h-full",
        "hover:scale-[1.02] hover:shadow-lg hover:border-brand-300 transition-all duration-300 ease-out",
        {
          "lg:col-span-3": documentsLength % 3 === 0,
          "md:col-span-2 lg:col-span-1": documentsLength % 2 === 0,
          "col-span-1": documentsLength % 3 !== 0,
          "min-h-96 lg:!col-span-3 md:!col-span-2": !hasDocuments,
        },
      )}
      onClick={onClickHandler}
    >
      <div className="flex flex-col items-center justify-center">
        <NWIcon name="file-plus" className="text-primary" width={90} height={90} />
        <h3 className="text-xl text-primary py-0.5">{hasDocuments ? "Add a project" : "Add your first project"}</h3>
        <p className="text-xs text-tertiary">Click to browse</p>
      </div>
    </button>
  );
}

function LimitReachedTile({ documentsLength = 0 }: { documentsLength: number }) {
  return (
    <button
      className={classNames(
        "border-2 border-dashed border-gray-200 rounded-[48px] flex p-4 items-center justify-center w-full h-full",
        "hover:scale-[1.02] hover:shadow-lg hover:border-brand-300 transition-all duration-300 ease-out",
        {
          "lg:col-span-3": documentsLength % 3 === 0,
          "md:col-span-2 lg:col-span-1": documentsLength % 2 === 0,
          "col-span-1": documentsLength % 3 !== 0,
        },
      )}
      onClick={() => openProModal("portfolio_add_project_limit")}
    >
      <div className="flex flex-col items-center justify-center">
        <NWIcon name="file-plus" className="text-quinary" width={90} height={90} />
        <h3 className="text-xl text-quinary py-0.5">Project limit reached</h3>
        <p className="text-xs text-quinary">
          Upgrade to{" "}
          <span className="font-semibold text-brand-400 hover:text-brand-500 transition-colors duration-300">PRO</span>{" "}
          to add more projects and showcase your skills
        </p>
      </div>
    </button>
  );
}
